import { bindEmailSend } from '../email-send'
import { offerStopCard, customerOfferCard, offerAgreementCard, offerReason, openCustomerOffer, type OfferFacts } from '../customer-offers'
import { addressLine } from '../address-words'
import { directionsHref } from '../../functions/lib/maps-link'
import { spotButton } from '../exact-spot'
import { lineDrawing } from '../picture-tiles'
import { CHOSEN_FIELDS, choicesOf, kindOf, wordsKey, type ChosenField, type Choices, type NoteKind } from '../note-kind'
import { parkingHours, parkingWords } from '../parking-hours'
import { customerInvoicesCard, invoicesForVisits, invoicesHere, STATE_WORDS, type InvoiceState } from '../invoices'
import { paidLook } from '../paid-moment'
import { openContactSheet, openEverySheet, openPriceSheet } from '../customer-sheets'
import { bookAgainCard, fetchCustomerBookAgain, usualGapWeeks } from '../book-again'
import { upNextSection, type UpNextStep } from '../up-next'
import { firstName } from '../late-message'
import { postcodeWords } from '../round-config'
import { customerContext, standingOf, takenOff } from '../customer-context'
import { jobTimesLine } from '../job-times'
import { kindLabel } from '../trade'
import { customerCalls } from '../calls'
import { api, dismiss, getQueue, mutate, withBase, type Ack } from '../api'
import { theirWords } from '../queue-chip'
import { openFollowupDialog } from '../followup-dialog'
import { askAgainLater, followupSummary } from '../followup'
import { confirmNeed, dueDate, effectiveCycle, isContact, needsReview, noFixedCycle } from '../planning'
import { bookAgainOn, businessSettings, visibleTime } from '../business-settings'
import { linkFor, wayFor, wayWords } from '../contact'
import { wayMenu } from '../contact-ui'
import { cashTakenBadge } from '../payment'
import { paymentWordsBadge, visitBadge } from '../visit-badge'
import { standingWords, visitWords, type WordsCustomer } from '../visit-words'
import { chaseCountOf, chaseStage } from '../chase-message'
import { askCashTaken } from '../cash-ask'
import { getMe, isWorker } from '../me'
import { collectingWords, creditLine, fromCredit, lineWords, moneyPending, toCollect, visitReceived, type CustomerMoney } from '../brought'
import { attachPhotos } from '../photo-note'
import { openBooking } from '../booking'
import { undoPatch } from '../undo'
import { routeGen } from '../route-state'
import { attachVoiceNote } from '../voice-note'
import { noteNode } from '../note-lines'
import { multilineField, textareaRows } from '../field-lines'
import type { Clean, Customer, Lead } from '../types'
import { blockedBadgeText, blockedNoteText, clear, cycleLabel, cycleNoteIsMeaningful, cycleOptions, cycleValueFor, dayLabel, el, loadError, money, nearestCycleChoice, nextBookedMessage, parseMoney, telHref, toast, todayISO, shownVisitTime } from '../ui'
import { eventSentence, eventTime, fieldChanges, NO_NAMES, parseRow, whoDidIt, type AuditEvent, type HistoryNames } from '../history'
import { analyse, weeksLabel } from '../customer-analysis'
import { shortDate } from '../ui'
import { loadRoundJobs, severalJobsOn } from '../job-names'
import { visitJobId, type CustomerJob } from '../jobs'
import { addJobButton, jobsBox, showsJobsBox, type JobsContext } from './customer-jobs'
import { openMessagesFor } from './rebook'
import { rowMenu } from '../row-menu'
import { lemonSwitch } from '../lemon-switch'
import { cancelVisitWithUndo, markTimeAgreed } from '../visit-actions'
import { agreedTimeKey, timeIsAgreed } from '../rebook-message'
import { reviewVisits } from '../diary-sets'
import { owedChip, paymentMessageWaiting } from '../owed-view'
import { priceWords } from '../inside-price'
import { jobTimesCard } from '../job-times-card'
import { missingDetails, openAddDetails, postcodeChoice, setPostcodeEnough } from '../add-details'
import { invoiceAfterEachJob } from '../commercial'
import { areaRenameLink } from '../area-rename'
import { areaOfCustomer } from '../month-view'

// Customer detail — everything about one customer: editable fields, clean
// history, booking creation, and THE VAN ACTION (booking → done with
// payment fields, a PATCH on the same row — never a second POST).

let customer: Customer | null = null
let cleans: Clean[] = []
// A customer Roundly took on as new: their first clean's visits and day (0082, functions/lib/first-clean.ts), for the job times.
let firstCleanData: { ids: number[]; date: string } | null = null
// Several jobs (step 2): this customer's extra jobs, running and stopped. Empty while the round's switch is off.
let jobs: CustomerJob[] = []
// Money brought from Squeegee (0043), only on a round that has it.
let broughtMoney: CustomerMoney | null = null
let customerId = 0
let editing = false
let refreshPending = false
// Where "back" goes: the screen the user navigated here from (router sets it
// at mount). 'new-jobs' keeps its own return so a lead's customer opens and
// comes back to the same filtered list.
let cameFrom = 'grid'
// #/customer/<id>?stand=1 (Work's Status cell): the page opens with Where they stand ready to change.
let openStanding = false


// Refresh discipline (codex HIGH-7 / kimi S-8): a background ack or the
// other owner's change must not wipe an editor mid-keystroke. While a
// field editor is open, refreshes defer to a reload when it closes.
function refresh(): void {
  if (editing) refreshPending = true
  else void load()
}

function afterEdit(): void {
  editing = false
  if (refreshPending) {
    refreshPending = false
    void load()
  }
}

// The 4xx terminal case for fire-and-forget mutations (codex CRITICAL-2):
// mutate() drops invalid edits from the queue and returns the ack — if the
// caller ignores it, a rejected Confirm/Chased/Paid vanishes silently.
function alert4xx(ack: Ack): void {
  if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
}

// ---- field editing (same no-optimistic discipline as the grid) -----------

interface FieldRow {
  label: string
  field: keyof Customer
  type?: 'text' | 'money' | 'int' | 'cycle'
  door?: boolean // shown even when empty — the van needs to know it's blank
}

// Ordered by what matters AT THE DOOR, not by table column order. Everything
// used to render flat and equal-weight, so "Parking" and "Job notes" — the two
// things Lewis needs before he gets out of the van — sat below Tier and Inside
// price, most of which are empty. `door: true` fields stay visible even when
// blank (an empty Parking is itself information); the rest hide when empty
// behind a "show all" toggle.
const FIELDS: FieldRow[] = [
  // Their name, changed here (Liam, 9 Oct 2026: "there is no where on this page to edit the customers name").
  { label: 'Name', field: 'name', door: true },
  { label: 'Address', field: 'address', door: true },
  { get label() { return postcodeWords('Postcode') }, field: 'postcode', door: true },
  { label: 'Phone', field: 'phone', door: true },
  // Where an email goes when email is how they are messaged (0036). Shown even when empty, so it can be added.
  { label: 'Email', field: 'email', door: true },
  { label: 'Parking', field: 'parking_restrictions', door: true },
  { label: 'Job notes', field: 'job_notes', door: true },
  { label: 'Preferred times', field: 'preferred_times', door: true },
  { label: 'Regular price', field: 'reg_price_pence', type: 'money', door: true },
  // The dropdown drives the NUMBER the app counts forward from. The free-text
  // note below it keeps detail the list can't hold ("insides every other
  // time", "March") without letting a typo break the date maths.
  { label: 'Cycle', field: 'cycle_days', type: 'cycle', door: true },
  // Only shown when it says more than the dropdown does (see renderFields).
  { label: 'Cycle note', field: 'cycle_text' },
  { label: 'Inside price', field: 'inside_price_pence', type: 'money' },
  { label: 'First clean price', field: 'first_clean_price_pence', type: 'money' },
  // Imported from the sheet and populated (cycle_days 109/261,
  // payment_increase 63/261) but rendered on NO screen — invisible and
  // uneditable, i.e. silently lost at the moment the sheet is retired.
  { label: 'Payment increase', field: 'payment_increase' },
  { label: 'Time taken', field: 'time_taken', door: true },
  { label: 'Area', field: 'area' },
  { label: 'Tier', field: 'tier' },
  { label: 'Call notes', field: 'call_notes' },
  // What they said the last time they were asked (0032): only the owner sees it.
  { label: 'What they said', field: 'said_note' },
  // Where they came from: the enquiry's source when they came through New jobs (Analysis shows it too).
  { label: 'Came from', field: 'origin' },
  { label: 'Notes', field: 'notes' },
]

// At the door (customer page redesign, Liam 9 Oct 2026): what the van needs before getting out, always in sight above
// the Visits · Details · Analysis switch. Details holds the rest; the Notes row is the Customer context box's own
// column (customers.notes), so it is edited there, not twice.
// Time taken (from the job sheet: the outside clean; inside is the same unless it says) joins them, in sight for planning a
// day (Liam, 9 Oct 2026: "i dont see anywhere for time taken on customers").
const DOOR_KEYS = new Set<keyof Customer>(['parking_restrictions', 'job_notes', 'preferred_times', 'time_taken'])
const DOOR_FIELDS = FIELDS.filter((f) => DOOR_KEYS.has(f.field))
const DETAIL_FIELDS = FIELDS.filter((f) => !DOOR_KEYS.has(f.field) && f.field !== 'notes')

let showAllFields = false
// Visits (the past ones and every change), Details (Customer context, Where they stand, the rest) or Analysis.
let tab: 'visits' | 'details' | 'analysis' = 'visits'

function fieldDisplay(c: Customer, f: FieldRow): string {
  const v = c[f.field]
  if (f.type === 'money') return money(v as number | null)
  if (f.type === 'cycle') return cycleLabel(c.cycle_days, c.cycle_text) || '—'
  // Tiers from job time (0070): what their timed jobs came to, and whether the tier is the owner's own choice.
  if (f.field === 'tier' && typeof c.tier_rate_pence === 'number') {
    const times = [typeof c.fronts_minutes === 'number' ? `fronts about ${c.fronts_minutes} min` : null, typeof c.backs_minutes === 'number' ? `backs about ${c.backs_minutes} min` : null,
      typeof c.inside_minutes === 'number' ? `insides about ${c.inside_minutes} min` : null].filter(Boolean)
    const rate = `${money(c.tier_rate_pence)} a minute from job times${times.length ? `, ${times.join(', ')}` : ''}`
    return v === null || v === undefined || String(v).trim() === '' ? rate : c.tier_by_hand ? `${String(v)} · you chose it (${rate})` : `${String(v)} · ${rate}`
  }
  return v === null || v === undefined ? '—' : String(v)
}

// A small pencil so a value reads as editable — the rows used to look like a
// printed table, and nobody tapped them.
function pencil(): HTMLElement {
  const wrap = el('span', { class: 'field-pencil', 'aria-hidden': 'true' })
  wrap.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>'
  return wrap
}

// "The postcode is enough" (tidy-up 2 item 3; Liam, 20 Oct 2026; build-messages.md 3.4): under Address while they have
// no street and a postcode, the choice the Diary's add box makes, ticked or not here, saved at once with Undo.
function postcodeEnoughLine(c: Customer): HTMLElement | null {
  const choice = isWorker() ? null : postcodeChoice(c)
  if (!choice) return null
  const words = postcodeWords("The postcode is enough (Roundly won't ask for their street)")
  const tick = el('input', { type: 'checkbox', 'aria-label': words }) as HTMLInputElement
  tick.checked = choice.on
  tick.addEventListener('change', () => { tick.disabled = true; void setPostcodeEnough(c, tick.checked, () => void load()) })
  return el('label', { class: 'field-row customer-postcode-enough' }, tick, el('span', {}, words))
}

function renderFields(container: HTMLElement, list: FieldRow[] = DETAIL_FIELDS, toggleEmpty = true): void {
  const c = customer!
  clear(container)
  const isEmpty = (f: FieldRow): boolean => {
    const v = c[f.field]
    return v === null || v === undefined || String(v).trim() === ''
  }
  const visible = (showAllFields && toggleEmpty ? list : list.filter((f) => f.door || !isEmpty(f))).filter(
    // The note is noise when it only repeats the dropdown ("8 weeks" against a
    // cycle of 8 weeks, "Adhoc" against ad-hoc) — 96 customers are exactly that.
    (f) => f.field !== 'cycle_text' || cycleNoteIsMeaningful(c.cycle_days, c.cycle_text),
  )
  const hidden = list.length - visible.length
  // Liam's "Just this time or always?": a note's Warning or Just a note is for every visit, until it is changed.
  if (visible.some((f) => (CHOSEN_FIELDS as string[]).includes(f.field as string) && String(c[f.field] ?? '').trim())) container.append(el('p', { class: 'note-kind-scope' }, `Warning or Just a note: for every visit of ${c.name}'s, until you change it.`))
  for (const f of visible) {
    const value = el('button', { type: 'button', class: 'field-value field-edit-button', 'aria-label': `Edit ${f.label}` }, el('span', { class: 'field-text' }, fieldDisplay(c, f)), pencil())
    value.addEventListener('click', () => {
      if (editing) return // one editor at a time — second tap was a dead input
      editing = true
      const cur = c[f.field]
      // Cycle is a fixed list, not free text: typing it produced 30 spellings
      // for 10 cycles, and one customer whose words said "8 weeks" with no
      // number behind them, so no date could be worked out for him.
      const input =
        f.type === 'cycle'
          ? (() => {
              const s = el('select', { class: 'cell-input' })
              const chosen = cycleValueFor(c.cycle_days, c.cycle_text)
              cycleOptions().forEach((o, i) => {
                const opt = el('option', { value: String(i) }, o.label)
                if (chosen === String(i)) opt.selected = true
                s.append(opt)
              })
              if (c.cycle_days && !cycleOptions().some((o) => o.days === c.cycle_days)) {
                const opt = el('option', { value: `d${c.cycle_days}` }, cycleLabel(c.cycle_days, null))
                opt.selected = true
                s.append(opt)
              }
              return s
            })()
          : multilineField(f.field, cur)
            ? el('textarea', { class: 'cell-input field-textarea', rows: String(textareaRows(cur)) })
            : el('input', { class: 'cell-input' })
      if (!(input instanceof HTMLSelectElement)) {
        input.value =
          f.type === 'money' && typeof cur === 'number' ? (cur / 100).toFixed(2) : cur === null || cur === undefined ? '' : String(cur)
      }
      // Nothing typed, nothing saved: opening a field and tapping away used to save it anyway (bug hunt 8 Oct 2026).
      const opened = input.value
      input.setAttribute('aria-label', f.label)
      if (input instanceof HTMLInputElement && f.type === 'money') input.inputMode = 'decimal'
      if (input instanceof HTMLInputElement && f.field === 'phone') input.type = 'tel'
      if (input instanceof HTMLInputElement && f.field === 'email') { input.type = 'email'; input.autocomplete = 'off' }
      value.replaceWith(input)
      input.focus()
      let done = false
      const bail = (): void => {
        // every exit path clears editing exactly once (kimi S-5's lesson)
        if (!done) {
          done = true
          afterEdit()
        }
      }
      const commit = (): void => {
        if (done) return
        if (input.value === opened) {
          bail()
          renderFields(container, list, toggleEmpty)
          return
        }
        done = true
        // The cycle dropdown writes BOTH fields together so the number and the
        // wording can never drift apart — one customer's words said "8 weeks"
        // with no number behind them, and no date could be worked out for him.
        let patch: Record<string, unknown>
        try {
          if (f.type === 'cycle') {
            const v = input.value
            if (v.startsWith('d')) {
              patch = { cycle_days: parseInt(v.slice(1), 10) }
            } else {
              const o = cycleOptions()[parseInt(v, 10)]
              const keepNote = cycleNoteIsMeaningful(c.cycle_days, c.cycle_text)
              patch = o
                // A choice with its own words (One off) saves them over a kept note, or it would read "Not set".
                ? { cycle_days: o.days, cycle_text: o.marker ?? (keepNote ? c.cycle_text : null) }
                : { cycle_days: null, cycle_text: null }
            }
          } else {
            const parsed =
              f.type === 'money' ? parseMoney(input.value) : input.value.trim() === '' ? null : input.value.trim()
            patch = { [f.field]: parsed }
          }
        } catch (err) {
          window.alert((err as Error).message)
          afterEdit()
          renderFields(container, list, toggleEmpty)
          return
        }
        const span = el('span', { class: 'field-value cell-pending' }, 'saving…')
        input.replaceWith(span)
        void mutate({
          label: `${f.label}: ${c.name}`,
          method: 'PATCH',
          path: `/api/customers/${c.id}`,
          // What the page showed when the box was changed (same-time saves): it saves unless someone changed this
          // same box first.
          body: withBase(c, { version: c.version, ...patch }),
          onAck: () => {
            afterEdit()
            void load()
          },
          onConflict: (ack) => {
            afterEdit()
            // Who changed it to what, when the server said (Liam, 15 Oct 2026).
            window.alert(ack.changed?.length ? `Not saved: ${ack.changed.map(theirWords).join(' and ')} first. Reloading.` : 'Changed somewhere else first. Reloading.')
            void load()
          },
          onQueued: () => {
            span.textContent = 'Waiting for signal'
            afterEdit()
          },
        }).then((ack) => {
          if (!ack.ok && ack.status >= 400 && ack.status < 500) {
            afterEdit()
            window.alert(ack.error ?? 'rejected')
            void load()
          }
        })
      }
      input.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key
        // In a note, Enter starts a new line; Ctrl or Cmd with Enter saves.
        if (k === 'Enter' && (input.tagName !== 'TEXTAREA' || (e as KeyboardEvent).metaKey || (e as KeyboardEvent).ctrlKey)) commit()
        if (k === 'Escape') {
          bail()
          renderFields(container, list, toggleEmpty)
          container.querySelector<HTMLButtonElement>(`button[aria-label="Edit ${f.label}"]`)?.focus()
        }
      })
      // A dropdown should save the moment you pick, not wait for a blur.
      if (input instanceof HTMLSelectElement) input.addEventListener('change', commit)
      input.addEventListener('blur', commit)
    })
    const fieldRow = el('div', { class: 'field-row' }, el('span', { class: 'field-label' }, f.label), value)
    // A note with words: a tile with its Warning / Just a note switch (0088, design A + B; Liam, 26 Oct 2026).
    const noteText = (CHOSEN_FIELDS as string[]).includes(f.field as string) ? String(c[f.field] ?? '').trim() : ''
    container.append(noteText ? noteKindTile(c, f.field as ChosenField, noteText, fieldRow) : fieldRow)
    if (f.field === 'address') { const enough = postcodeEnoughLine(c); if (enough) container.append(enough) }
    // Their Area is theirs to change here; "Rename N11 for everyone" renames the whole area (area-rename.ts, Liam's pick
    // A, 27 Oct 2026).
    // Under Area when it is shown; under the address when it is not (most customers' area is their postcode's, with the
    // Area box empty and hidden: the Help session, 27 Oct 2026).
    const areaRow = f.field === 'area' || (f.field === 'address' && !visible.some((v) => v.field === 'area'))
    if (areaRow && getMe()?.role === 'owner' && !(c as { details_binned_at?: string | null }).details_binned_at && areaOfCustomer(c) !== 'Not set') {
      container.append(el('div', { class: 'field-row customer-area-rename' }, areaRenameLink(areaOfCustomer(c), `Rename ${areaOfCustomer(c)} for everyone`)))
    }
  }
  if (toggleEmpty && (hidden > 0 || showAllFields)) {
    const toggle = el(
      'button',
      { class: 'field-toggle' },
      showAllFields ? 'Show less' : `Show ${hidden} empty field${hidden > 1 ? 's' : ''}`,
    )
    toggle.addEventListener('click', () => {
      showAllFields = !showAllFields
      renderFields(container, list, toggleEmpty)
    })
    container.append(el('div', { class: 'field-row field-row-toggle' }, toggle))
  }
}

// Warning or just a note (0088, src/note-kind.ts): the note's tile, red or grey, with the switch on it and why it is what it
// is. The choice is kept with the words it was made for, for every visit of theirs, until the words change.
function noteKindTile(c: Customer, field: ChosenField, text: string, row: HTMLElement): HTMLElement {
  const choices = choicesOf((c as Customer & { note_kinds?: string | null }).note_kinds)
  const k = kindOf(field, text, choices)
  const hours = field === 'parking_restrictions' ? parkingHours(text) : null
  // A chosen Warning on parking with hours still follows them, and says so (Sonnet's notes review 9).
  const why = k.chosen ? (hours && k.kind === 'warn' ? `You chose Warning: red on a visit ${parkingWords(hours).startsWith('before') ? parkingWords(hours) : `between ${parkingWords(hours).replace(' to ', ' and ')}`}, or with no time yet. If the words change, Roundly guesses again.` : 'You chose this. If the words change, Roundly guesses again.')
    : hours && k.kind === 'warn' ? `Red on a visit ${parkingWords(hours).startsWith('before') ? parkingWords(hours) : `between ${parkingWords(hours).replace(' to ', ' and ')}`}, or with no time yet. Grey at other times.`
    : k.word ? `Roundly's guess: it says "${k.word}".` : "Roundly's guess."
  const seg = el('div', { class: 'note-kind-seg', role: 'group', 'aria-label': `How Today shows ${field === 'parking_restrictions' ? 'parking' : 'this note'}` })
  const choose = (kind: NoteKind, b: HTMLButtonElement): void => {
    if (kind === k.kind && k.chosen) return
    for (const x of seg.querySelectorAll('button')) (x as HTMLButtonElement).disabled = true
    // Only this note's choice: the server merges it with the others (two quick taps never wipe each other).
    const next: Choices = { [field]: { kind, for: wordsKey(text) } }
    void mutate({
      label: `${kind === 'warn' ? 'Warning' : 'Just a note'}: ${c.name}`, method: 'PATCH', path: `/api/customers/${c.id}`,
      body: withBase(c, { version: c.version, note_kinds: next }),
      onAck: () => void load(),
      onConflict: () => { window.alert('Changed somewhere else first. Reloading.'); void load() },
      // No signal: shown chosen now, saved when it can (Sonnet's notes review 9).
      onQueued: () => {
        for (const x of seg.querySelectorAll('button')) { x.classList.remove('note-kind-on-warn', 'note-kind-on-note'); x.setAttribute('aria-pressed', 'false') }
        b.classList.add(`note-kind-on-${kind}`); b.setAttribute('aria-pressed', 'true')
        tileEl.classList.toggle('note-kind-tile-warn', kind === 'warn')
        whyEl.textContent = 'Saved on this phone: it sends when you have signal.'
      },
    }).then((ack) => { if (!ack.ok && ack.status >= 400 && ack.status < 500) { window.alert(ack.error ?? 'rejected'); void load() } })
  }
  for (const [kind, words] of [['warn', '! Warning'], ['note', 'Just a note']] as const) {
    const on = k.kind === kind
    const b = el('button', { type: 'button', class: `note-kind-btn${on ? ` note-kind-on-${kind}` : ''}`, 'aria-pressed': String(on) }, words) as HTMLButtonElement
    b.addEventListener('click', () => choose(kind, b))
    seg.append(b)
  }
  const whyEl = el('p', { class: 'note-kind-why' }, why)
  const tileEl = el('div', { class: `note-kind-tile${k.kind === 'warn' ? ' note-kind-tile-warn' : ''}` }, row, seg, whyEl)
  return tileEl
}

// ---- the van action --------------------------------------------------------

// booking → done: inline payment row, then one PATCH on the same clean row.
function markPaid(cl: Clean, m: 'cash' | 'bank', taken: number | null = null): void {
  // On a round with brought money (0043) every payment carries the day it was paid, and cash what was taken.
  const moneyRound = getMe()?.broughtMoney === true
  void mutate({
    label: `Mark paid: ${customer!.name} ${cl.date}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, payment_status: 'paid', payment_method: m, chased: 0, ...(moneyRound ? { paid_on: todayISO() } : {}), ...(taken !== null ? { taken_pence: taken } : {}) },
    onAck: () => refresh(),
    onConflict: () => {
      window.alert('Changed somewhere else first. Reloading.')
      refresh()
    },
  }).then((ack) => {
    alert4xx(ack)
    undoPatch({ text: `Marked ${customer!.name} paid by ${m}`, label: `Undo paid: ${customer!.name} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
      body: { payment_status: cl.payment_status, payment_method: cl.payment_method ?? null, chased: cl.chased }, after: () => refresh() })
  })
}

// The opposite of markPaid, and the reason Owed's "not recorded" pile had no
// exit: chased is cleared with it, because owing money is not being chased
// for it.
function markUnpaid(cl: Clean): void {
  void mutate({
    label: `Mark unpaid: ${customer!.name} ${cl.date}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, payment_status: 'unpaid', chased: 0 },
    onAck: () => refresh(),
    onConflict: () => {
      window.alert('Changed somewhere else first. Reloading.')
      refresh()
    },
  }).then((ack) => {
    alert4xx(ack)
    undoPatch({ text: `Marked ${customer!.name} not paid`, label: `Undo not paid: ${customer!.name} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
      body: { payment_status: cl.payment_status, payment_method: cl.payment_method ?? null, chased: cl.chased }, after: () => refresh() })
  })
}

// Where the customer stands and what they were asked, for one visit's words (visit-words.ts), as the Diary reads them
// (diaryCustomer): the customer's own record, that job's last done visit, and an extra job's own messages count (bug
// hunt 8 Oct 2026: this page said "confirmed" where the Diary said "Day agreed · time to confirm").
function wordsCustomer(cl: Clean): WordsCustomer | null {
  if (!customer) return null
  const jobId = visitJobId(cl)
  const lastDone = cleans.filter((x) => x.state === 'done' && visitJobId(x) === jobId).map((x) => x.date).sort().at(-1) ?? null
  const job = jobId === null ? null : jobs.find((j) => j.id === jobId)
  return { ...customer, last_clean: lastDone, ...(job ? { rebook_messages: job.rebook_messages, rebook_messaged_on: job.rebook_messaged_on } : {}) }
}

// The word on a visit: its job's name once the customer has more than one job (several jobs), else the trade's
// word for its kind, as always.
function visitWord(cl: Clean): string {
  if (!jobs.some((j) => !j.deleted_at) && !customer?.job_stopped_on) return kindLabel(cl.kind)
  const id = visitJobId(cl)
  if (id === null) return customer?.job_name?.trim() || kindLabel(cl.kind)
  return jobs.find((j) => j.id === id)?.name ?? kindLabel(cl.kind)
}

// ---- the visits ----------------------------------------------------------------------------------------------------
//
// Customer page redesign (Liam approved the drawings, 9 Oct 2026; checked with a second model against the code and the
// truth docs: FIXES-20261008/LIAM-customer-page-design.md, MERGED POSITION). A visit that asks for something (a past
// visit to review, the next visit and the others booked, a contact reminder) is a card with its one main button; a
// past visit is one line that opens its details; a visit not paid is a card at the top. Every save is the one the
// Diary or Owed makes, with the same Undo.

// The visit with its customer's name on it, for the saves and pop-ups that say who it is (the page's own read leaves
// customer_name off).
function named(cl: Clean): Clean {
  return { ...cl, customer_name: cl.customer_name ?? customer?.name ?? undefined }
}

// A visit's words, as the Diary draws them (visit-words.ts): its job, money brought over, where it stands or how it was
// paid, the door outcome, who did it and when.
function visitBadges(cl: Clean): HTMLElement {
  const badges = el('span', { class: 'clean-badges' })
  badges.append(el('span', { class: 'badge' }, visitWord(cl)))
  // Money brought from Squeegee (0043): the last visit brought over is in the opening balance (Liam Q6), and credit
  // on a visit shows what is left to collect.
  if (cl.carryover === 1) badges.append(el('span', { class: 'badge badge-unknown' }, 'From Squeegee · in opening balance'))
  if (moneyPending(cl) && cl.state === 'booked') badges.append(el('span', { class: 'badge credit-badge credit-pending' }, 'Credit being worked out'))
  else if (fromCredit(cl) > 0 && cl.amount_pence != null && cl.state === 'booked') badges.append(el('span', { class: 'badge credit-badge' }, `${money(fromCredit(cl))} from credit · ${money(toCollect(cl))} to collect`))
  if (cl.state === 'booked') {
    badges.append(visitBadge(cl, wordsCustomer(cl)))
    // The door outcome (step 4): still booked, but the owner sees why it did not happen, and the worker's note.
    if (cl.blocked_reason) {
      badges.append(el('span', { class: 'badge badge-blocked' }, blockedBadgeText(cl)))
      const blockedNote = blockedNoteText(cl.blocked_reason, cl.blocked_note)
      if (blockedNote) badges.append(el('span', { class: 'clean-note' }, blockedNote))
    }
  } else if (cl.state === 'done') {
    if (cl.carryover !== 1) badges.append(paymentWordsBadge(cl))
    const cashTaken = cashTakenBadge(cl)
    if (cashTaken) badges.append(cashTaken)
  } else {
    // The Diary's words for a cancelled visit (visit-words.ts), not a word of this page's own.
    badges.append(el('span', { class: 'badge badge-cancelled' }, visitWords(cl, wordsCustomer(cl)).text))
  }
  if (cl.worker) badges.append(el('span', { class: 'badge badge-worker' }, cl.worker))
  // Job times (0020): when the worker got there and how long it took.
  const jobTimes = jobTimesLine(cl)
  if (jobTimes) badges.append(el('span', { class: 'badge badge-job-times' }, jobTimes))
  return badges
}

// Mark confirmed: the Diary's rule (planning.ts confirmNeed: only a coming visit whose day still needs agreeing, never
// Roundly's suggestion, a contact reminder or a visit to check first) and the Diary's Undo.
function markConfirmedButton(cl: Clean): HTMLElement {
  const confirm = el('button', { class: 'btn-primary' }, 'Mark confirmed')
  confirm.addEventListener('click', () => {
    confirm.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Confirm booking: ${customer!.name} ${cl.date}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, confirmed: 1 },
      onAck: () => refresh(),
      onConflict: () => refresh(),
    }).then((ack) => {
      if (!ack.ok && ack.status >= 400 && ack.status < 500) confirm.removeAttribute('disabled')
      alert4xx(ack)
      undoPatch({ text: `Marked ${customer!.name} confirmed`, label: `Undo confirmed: ${customer!.name} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
        body: { confirmed: 0 }, after: () => refresh() })
    })
  })
  return confirm
}

// Paid · Cash · Bank: one control with two answers, as Owed and the Diary have it. Cash asks what was taken on a round
// with brought money (Astra r2, finding 12). main: the card's one main control (lemon), else plain.
function paidPair(cl: Clean, host: HTMLElement, main = false): HTMLElement {
  const pair = el('div', { class: `cust-paid${main ? ' cust-paid-main' : ''}`, role: 'group', 'aria-label': `Paid, for the ${dayLabel(cl.date)} visit` }, el('span', { class: 'cust-paid-label' }, 'Paid'))
  for (const m of ['cash', 'bank'] as const) {
    const paid = el('button', { type: 'button', 'aria-label': `Paid ${m}` }, m === 'cash' ? 'Cash' : 'Bank')
    // The one Cash and Bank pair, with its picture and colour, as Owed and the Diary (docs/plans/one-look/PLAN.md 1).
    paidLook(paid, m)
    paid.addEventListener('click', () => {
      if (m === 'cash' && getMe()?.broughtMoney === true) {
        askCashTaken(host, toCollect(cl) ?? 0, (pence) => { paid.setAttribute('disabled', 'disabled'); markPaid(cl, m, pence) })
        return
      }
      paid.setAttribute('disabled', 'disabled')
      markPaid(cl, m)
    })
    pair.append(paid)
  }
  return pair
}

// A visit that asks for something: to review (its day passed and nobody said), the next visit and the others booked,
// a contact reminder. Its one main button is the Diary's next step (schedule.ts visitNextStep): Done on its day or
// after, and Not done beside it on a visit to review; Mark confirmed or Time agreed before it.
function visitCard(cl: Clean, kind: 'review' | 'next' | 'upcoming' | 'contact'): HTMLElement {
  const card = el('div', { class: `clean-row clean-${cl.state} cust-visit cust-visit-${kind}`, 'data-clean-id': String(cl.id) })
  const today = todayISO()
  const future = cl.date > today
  if (kind === 'review') card.append(el('span', { class: 'cust-ask-label' }, 'Past visit to review'))
  // A visit marked "couldn't do it" says so first (the worker's reason is in its words), never "Nothing booked".
  const when = cl.blocked_reason ? `Couldn't do ${dayLabel(cl.date)}` : `${dayLabel(cl.date)}${shownVisitTime(cl) ? ` · ${shownVisitTime(cl)}` : ''}`
  card.append(el('div', { class: 'cust-visit-head' }, el('span', { class: 'clean-date cust-visit-when' }, when), el('span', { class: 'clean-amount cust-visit-price' }, priceWords(cl) ?? money(cl.amount_pence))))
  card.append(visitBadges(cl))
  if (cl.note) card.append(noteNode(cl.note))
  const actions = el('div', { class: 'clean-actions cust-visit-actions' })
  if (!isContact(cl) && !future) {
    const done = el('button', { class: 'btn-primary' }, 'Done')
    // The same "How was it paid?" pop-up as Owed and the Diary (Liam, 9 Oct 2026); the full form is behind its link.
    done.addEventListener('click', () => void import('./owed').then(({ openDonePopup }) => openDonePopup(named(cl))))
    actions.append(done)
  }
  if (kind === 'review') {
    // The Diary's own Not done question for this visit (Book them again, Cancel this visit, Off the
    // round): the Diary opens with it, so the answers and their saves are the Diary's.
    const notDone = el('button', { type: 'button', class: 'cust-not-done' }, 'Not done')
    notDone.addEventListener('click', () => { window.location.hash = `#/schedule?notdone=${cl.id}` })
    actions.append(notDone)
  }
  const need = future && !isContact(cl) ? confirmNeed(cl, customer) : null
  if (need === 'day') actions.append(markConfirmedButton(cl))
  else if (future && cl.confirmed && agreedTimeKey(cl.date, visibleTime(cl)) && !timeIsAgreed(cl) && !isContact(cl) && need !== 'check') {
    // The day agreed and a time set but not agreed: the Diary's one-tap Time agreed, its save and Undo (visit-actions.ts).
    const agree = el('button', { type: 'button', class: 'btn-primary' }, 'Time agreed')
    agree.addEventListener('click', () => markTimeAgreed(named(cl), agree, () => refresh()))
    actions.append(agree)
  }
  if (kind !== 'review') {
    const edit = el('button', { type: 'button' }, isContact(cl) ? 'Plan visit' : 'Edit visit')
    edit.addEventListener('click', () => openBooking({ customerId: cl.customer_id, cleanId: cl.id }))
    actions.append(edit)
  }
  // Voice note and photos, the same parts the worker's Today card uses; the strip of what the visit carries stays on
  // the card, the buttons go in its ⋯ with Cancel visit (the Diary's, with its Undo; visit-actions.ts).
  const photos = el('div', { class: 'card-photos' })
  actions.append(rowMenu(`More for the ${dayLabel(cl.date)} visit`, (list, close) => {
    if (kind === 'review') {
      const edit = el('button', { type: 'button' }, 'Edit visit')
      edit.addEventListener('click', () => { close(); openBooking({ customerId: cl.customer_id, cleanId: cl.id }) })
      list.append(edit)
    }
    const voice = el('button', { type: 'button', class: 'voice-note-btn' })
    attachVoiceNote(voice, { clean: { id: cl.id, version: cl.version }, onSaved: (row) => { cl.version = row.version; refresh() } })
    list.append(voice)
    attachPhotos(photos, { clean: { id: cl.id }, buttonHost: list })
    const cancel = el('button', { type: 'button', class: 'row-menu-danger' }, 'Cancel visit')
    cancel.addEventListener('click', () => cancelVisitWithUndo(named(cl), { close, changed: () => refresh(), refused: () => void load() }))
    list.append(cancel)
  }))
  card.append(photos, actions)
  return card
}

// A done visit not paid, at the top of the page: Owed's own words (owed-view.ts), Paid · Cash · Bank as its one main
// control (the Diary's next step for done work not paid), Open in Owed for the message, and the by-hand count in
// Owed's words for where the visit is.
function owedCard(cl: Clean): HTMLElement {
  const today = todayISO()
  const card = el('section', { class: 'clean-row clean-done cust-owed', 'data-clean-id': String(cl.id), 'aria-label': `Not paid, ${dayLabel(cl.date)}` })
  card.append(el('span', { class: 'cust-ask-label' }, 'Not paid'))
  card.append(el('div', { class: 'cust-visit-head' }, el('span', { class: 'clean-date cust-visit-when' }, dayLabel(cl.date)), el('span', { class: 'clean-amount cust-visit-price' }, money(cl.amount_pence))))
  const chip = owedChip(cl, today, shortDate)
  if (chip && chip.kind !== 'unknown') card.append(el('span', { class: `cust-owed-chip owed-chip-${chip.kind}` }, chip.text))
  // A commercial customer's visit Owed lists under "Invoice to send" (commercial AGREED.md 9; the server's rule,
  // functions/api/owed.ts invoice_to_send: invoiced each visit, Invoices on, priced, not brought over): the invoice asks
  // them, never the payment message or a chase, so their page says so too (build-money, 21 Oct 2026). Open in Owed sends it.
  const invoiceDue = cl.payment_status === 'unpaid' && (cl.amount_pence ?? 0) > 0 && cl.carryover !== 1 && invoiceAfterEachJob(customer)
  const waiting = !invoiceDue && paymentMessageWaiting(cl, today)
  if (invoiceDue) {
    // Only while the visit is on no SENT invoice, as Owed (Sonnet's recheck S2, GPT-6.1's G7, 21 Oct 2026): a sent one
    // shows its number and where it stands; one made and not sent yet is still to send.
    const cue = el('p', { class: 'cust-owed-cue' }, 'Invoice to send')
    card.append(cue)
    void invoicesForVisits([cl.id]).then((on) => {
      const o = on[String(cl.id)] as ({ number: string; state: InvoiceState; sent_at?: string | null } | undefined)
      const sent = o ? ('sent_at' in o ? !!o.sent_at : o.state !== 'not_sent') : false
      if (o && sent && cue.isConnected) cue.textContent = `${o.number} · ${STATE_WORDS[o.state]}`
    })
  } else if (waiting) {
    // On an invoice already (one made from More > Invoices, or their card): the invoice asks them, as Owed says, so the
    // payment message is not offered beside it (GPT-6.1, docs/plans/invoice-maker/PLAN.md).
    const cue = el('p', { class: 'cust-owed-cue' }, 'Payment message to send')
    card.append(cue)
    void invoicesForVisits([cl.id]).then((on) => {
      const o = on[String(cl.id)] as ({ number: string; state: InvoiceState; sent_at?: string | null } | undefined)
      if (!o || !o.sent_at || !cue.isConnected) return
      cue.textContent = `${o.number} · ${STATE_WORDS[o.state]}`
      // The invoice is chased, never the visit (Owed leaves it out of the visit chase): no "I sent it myself" (GPT-6.1).
      card.querySelector('.cust-chase-self')?.remove()
    })
  }
  if (cl.note) card.append(noteNode(cl.note))
  card.append(paidPair(cl, card, true))
  const more = el('div', { class: 'clean-actions cust-owed-more' })
  const owe = el('button', { type: 'button', class: 'btn-secondary' }, 'Open in Owed')
  owe.addEventListener('click', () => void import('./owed').then(({ openOwedFor }) => openOwedFor(cl.id)))
  more.append(owe)
  // Chasing is for work someone has CONFIRMED is unpaid. The same save and Undo as Owed's by-hand button, in Owed's
  // words for the stage (owed.ts): "I sent it myself" for the payment message, "I chased them myself" after it. The
  // chase is counted and dated, so Owed waits its week before offering the next one. Offered only while a chase is due.
  if (!invoiceDue && cl.payment_status === 'unpaid' && chaseStage(cl, todayISO()) === 'send') {
    const n = chaseCountOf(cl)
    const chase = el('button', { type: 'button', class: 'link-button cust-chase-self' }, waiting ? 'I sent it myself' : 'I chased them myself')
    chase.addEventListener('click', () => {
      chase.setAttribute('disabled', 'disabled')
      void mutate({
        label: `Chase ${n + 1}: ${customer!.name} ${cl.date}`,
        method: 'PATCH',
        path: `/api/cleans/${cl.id}`,
        body: { version: cl.version, chased: 1, chase_count: n + 1, chased_on: todayISO() },
        onAck: () => refresh(),
        onConflict: () => refresh(),
      }).then((ack) => {
        if (!ack.ok && ack.status >= 400 && ack.status < 500) chase.removeAttribute('disabled')
        alert4xx(ack)
        undoPatch({ text: `Counted ${waiting ? 'the payment message' : 'a chase'} by hand to ${customer!.name}`, label: `Undo chase: ${customer!.name} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
          body: { chased: cl.chased, chase_count: n, chased_on: cl.chased_on ?? null }, after: () => refresh() })
      })
    })
    more.append(chase)
  }
  card.append(more)
  return card
}

// A past visit: one line (its day, time, words, price, the first line of its note) that opens its details. A visit
// that still needs an answer keeps that answer on the line, one tap as before: a payment nobody recorded (Paid · Cash
// · Bank and Not paid), a booked visit whose day has passed that the Diary does not ask about (Done, Edit visit).
function pastRow(cl: Clean): HTMLElement {
  const row = el('div', { class: `clean-row clean-${cl.state} cust-past`, 'data-clean-id': String(cl.id) })
  const open = el('button', { type: 'button', class: 'cust-past-open', 'aria-label': `The ${dayLabel(cl.date)} visit: details, voice notes and photos` })
  const main = el('span', { class: 'clean-main' })
  main.append(el('span', { class: 'clean-date' }, dayLabel(cl.date)))
  if (shownVisitTime(cl)) main.append(el('span', { class: 'clean-time' }, shownVisitTime(cl)!))
  main.append(visitBadges(cl))
  const note = cl.note?.split(/\r\n|\r|\n/).find((l) => l.trim())
  if (note) main.append(el('span', { class: 'cust-past-note' }, note))
  open.append(main, el('span', { class: 'clean-amount' }, priceWords(cl) ?? money(cl.amount_pence)), el('span', { class: 'cust-chevron', 'aria-hidden': 'true' }))
  open.addEventListener('click', () => openVisitSheet(cl))
  row.append(open)
  const actions = el('div', { class: 'clean-actions' })
  if (cl.state === 'booked' && !isContact(cl)) {
    const done = el('button', { class: 'btn-primary' }, 'Done')
    done.addEventListener('click', () => void import('./owed').then(({ openDonePopup }) => openDonePopup(named(cl))))
    const edit = el('button', { type: 'button' }, 'Edit visit')
    edit.addEventListener('click', () => openBooking({ customerId: cl.customer_id, cleanId: cl.id }))
    actions.append(done, edit)
  }
  // 'unknown' gets the control too: it had none, so £2,907 of completed work was unresolvable from the UI. A visit in
  // an opening balance (0043) is paid or owed on the account, never on the visit itself. A visit known not paid has
  // its card at the top of the page instead.
  if (cl.state === 'done' && cl.payment_status !== 'paid' && cl.carryover !== 1) {
    if (cl.payment_status === 'unknown') actions.append(paidPair(cl, row))
    // The missing half of the decision: nothing here could record that the money never arrived, so a visit logged
    // without a payment stayed "not recorded" forever and never reached the chase list.
    if (cl.payment_status !== 'unpaid') {
      const notPaid = el('button', {}, 'Not paid')
      notPaid.addEventListener('click', () => {
        notPaid.setAttribute('disabled', 'disabled')
        markUnpaid(cl)
      })
      actions.append(notPaid)
    }
  }
  if (actions.children.length) row.append(actions)
  return row
}

// A past visit's details: its words, note, job times and photos, and Voice note and Photo to add to it.
function openVisitSheet(cl: Clean): void {
  const previous = document.activeElement as HTMLElement | null
  const dialog = el('dialog', { class: 'booking-dialog cust-visit-dialog', 'aria-label': `The ${dayLabel(cl.date)} visit` })
  const close = el('button', { type: 'button' }, 'Close')
  const body = el('div', { class: 'cust-visit-sheet' })
  body.append(el('p', { class: 'cust-visit-sheet-sub' }, priceWords(cl) ?? money(cl.amount_pence)), visitBadges(cl))
  if (cl.note) body.append(noteNode(cl.note))
  if (cl.state !== 'cancelled') {
    const photos = el('div', { class: 'card-photos' })
    const actions = el('div', { class: 'clean-actions cust-visit-sheet-actions' })
    const voice = el('button', { type: 'button', class: 'voice-note-btn' })
    attachVoiceNote(voice, { clean: { id: cl.id, version: cl.version }, onSaved: (row) => { cl.version = row.version; refresh() } })
    actions.append(voice)
    attachPhotos(photos, { clean: { id: cl.id }, buttonHost: actions })
    body.append(photos, actions)
  }
  dialog.append(el('header', { class: 'booking-head' }, el('h2', {}, `${dayLabel(cl.date)}${shownVisitTime(cl) ? ` · ${shownVisitTime(cl)}` : ''}`), close), body)
  close.addEventListener('click', () => dialog.close())
  dialog.addEventListener('close', () => { dialog.remove(); if (previous?.isConnected) previous.focus({ preventScroll: true }) })
  document.body.append(dialog)
  dialog.showModal()
}

// The nearest how-often to a number of days ("Set to 4 months" for about 18 weeks): ui.ts nearestCycleChoice.
const nearestCycle = nearestCycleChoice
// Their cycle set from the page (the "Every" box and Analysis' Rhythm): the same save as Details' Cycle, with Undo.
function setCycleTo(c: Customer, days: number, label: string): void {
  void mutate({ label: `Cycle: ${c.name}`, method: 'PATCH', path: `/api/customers/${c.id}`, body: { version: c.version, cycle_days: days }, onConflict: () => void load() }).then((ack) => {
    if (!ack.ok && ack.status !== 0) { if (ack.status >= 400 && ack.status < 500) toast(`Couldn't set ${c.name}'s cycle: ${ack.error ?? 'refused'}`); void load(); return }
    void load()
    undoPatch({ text: `${c.name}: every ${label}`, label: `Undo cycle: ${c.name}`, path: `/api/customers/${c.id}`, ack, version: c.version, body: { cycle_days: null }, after: () => void load() })
  })
}

// Three figures at the top of a customer's page, all from what Roundly holds (Astra review 2026-09-25: nothing it
// cannot know, such as "paid on time"): the price, how often, and what they have paid, with the last paid visit.
function customerFigures(c: Customer, all: Clean[], underPrice: HTMLElement | null = null, setCycle?: (days: number, label: string) => void, edit?: { price: () => void; every: () => void }): HTMLElement {
  const tile = (k: string, v: string, sub?: Node | string | null): HTMLElement =>
    el('div', { class: 'customer-tile' }, el('div', { class: 'tile-k' }, k), el('div', { class: 'tile-v' }, v), sub ? el('div', { class: 'tile-s' }, sub) : null)
  // Price and Every open to change with one tap (Liam's testers, 19 Oct 2026, customer-sheets.ts); the word under the
  // figure says so. Anything else the tile carries (the guess's "Set to 4 weeks") sits outside the tap.
  const tapTile = (k: string, v: string, sub: string | null, act: () => void): HTMLElement => {
    const word = v === 'Not set' ? 'Set it' : 'Change'
    const tap = el('button', { type: 'button', class: 'customer-tile-tap', 'aria-label': `${k}: ${v}. ${word}` },
      el('div', { class: 'tile-k' }, k), el('div', { class: 'tile-v' }, v), sub ? el('div', { class: 'tile-s' }, sub) : null, el('span', { class: 'tile-change' }, word))
    tap.addEventListener('click', act)
    return el('div', { class: 'customer-tile customer-tile-has-tap' }, tap)
  }
  const row = el('div', { class: 'customer-tiles cust-figures' })
  const extras = [c.first_clean_price_pence != null ? `first clean ${money(c.first_clean_price_pence)}` : null, c.inside_price_pence != null ? `inside ${money(c.inside_price_pence)}` : null].filter(Boolean).join(' · ')
  const priceWords = c.reg_price_pence != null ? money(c.reg_price_pence) : 'Not set'
  const priceTile = edit && !underPrice ? tapTile('Price', priceWords, extras || null, edit.price) : tile('Price', priceWords, extras || null)
  // Several jobs (Liam Q2): "Add another job" sits under the price.
  if (underPrice) priceTile.append(underPrice)
  row.append(priceTile)
  // The customer's own page loads the plain row, without the list's aggregates, so the last clean and the guessed
  // cycle are worked out here from the visits, by the list's own rule (Astra review 2026-09-25).
  const doneDates = all.filter((x) => x.state === 'done').map((x) => x.date).sort().reverse()
  const recent = doneDates.slice(0, 4)
  const gaps: number[] = []
  for (let i = 0; i + 1 < recent.length; i++) {
    const gap = Math.round((Date.parse(recent[i]) - Date.parse(recent[i + 1])) / 86_400_000)
    if (gap >= 14) gaps.push(gap)
  }
  const withAggregates: Customer = {
    ...c,
    last_clean: c.last_clean ?? doneDates[0] ?? null,
    cycle_estimate_days: c.cycle_estimate_days ?? (gaps.length ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : null),
  }
  const cyc = effectiveCycle(withAggregates)
  const due = dueDate(withAggregates)
  // Several jobs: the main job stopped while the customer stays (their gutters carry on). Nothing is due for it.
  if (c.job_stopped_on) row.append(tile('Every', 'Stopped', `${(c.job_name?.trim() || kindLabel('window')).toLowerCase()} stopped ${shortDate(c.job_stopped_on)}`))
  else {
    // A guess in Analysis' words ("about 4 months", as Rhythm says it), not a second way of putting it (Liam, 9 Oct 2026).
    const everyWords = cycleLabel(c.cycle_days, c.cycle_text) || (cyc.days ? `about ${weeksLabel(cyc.days)}` : 'Not set')
    const everySub = [cyc.guessed ? 'guessed from past visits' : null, due ? `due ${shortDate(due)}` : null].filter(Boolean).join(' · ') || null
    const every = edit ? tapTile('Every', everyWords, everySub, edit.every) : tile('Every', everyWords, everySub)
    // A guess with no cycle set (Liam, 9 Oct 2026): one tap sets the nearest choice the Cycle list has, so Details and
    // the due date stop relying on a guess.
    if (setCycle && cyc.guessed && cyc.days && !c.cycle_days && !cycleLabel(c.cycle_days, c.cycle_text)) {
      const nearest = nearestCycle(cyc.days)
      if (nearest) {
        const set = el('button', { type: 'button', class: 'link-button cust-set-cycle' }, `Set to ${nearest.label}`)
        set.addEventListener('click', () => { set.disabled = true; setCycle(nearest.days, nearest.label) })
        every.append(set)
      }
    }
    row.append(every)
  }
  const done = all.filter((x) => x.state === 'done').sort((a, b) => b.date.localeCompare(a.date))
  const lastPaid = done.find((x) => x.payment_status === 'paid')
  // A paid visit with no amount recorded is counted, not treated as £0.
  const paidVisits = done.filter((x) => x.payment_status === 'paid')
  // Money actually received (0043): credit used is not paid, cash part paid is (as before with no credit).
  const paid = done.reduce((sum, x) => sum + visitReceived(x), 0)
  const noAmount = paidVisits.filter((x) => x.amount_pence == null).length
  const first = done[done.length - 1]
  const paidSub = [
    done.length ? `${done.length} visit${done.length === 1 ? '' : 's'} since ${shortDate(first.date)}` : null,
    noAmount && paidVisits.length > noAmount ? `plus ${noAmount} with no amount recorded` : null,
    lastPaid ? `last paid ${shortDate(lastPaid.date)}${lastPaid.payment_method ? `, ${lastPaid.payment_method === 'cash' ? 'cash' : 'bank'}` : ''}` : null,
  ].filter(Boolean).join(' · ')
  row.append(tile('Paid to date', paidVisits.length && noAmount === paidVisits.length ? 'Amount not recorded' : money(paid), paidSub || null))
  return row
}

// Money brought from Squeegee (0043; PLAN section 6): what they owe or hold in credit, the direct debits still
// collecting, and the history brought over (owner only, read-only).
const LINES_SHOWN = 8
function moneyPanel(m: CustomerMoney): HTMLElement {
  const box = el('section', { class: 'money-panel', 'aria-label': 'Money' })
  const credit = creditLine(m, shortDate, money)
  if (credit) box.append(el('p', { class: 'credit-line' }, credit))
  for (const a of m.accounts) {
    box.append(el('h3', {}, 'From Squeegee'))
    const left = a.left_pence
    box.append(el('div', { class: 'money-figure' }, el('span', {}, left > 0 ? 'Still owed on their account' : left < 0 ? 'In credit on their account' : 'Account settled'), el('strong', {}, money(Math.abs(left)))))
    box.append(el('div', { class: 'money-sub' }, `Brought over ${money(Math.abs(a.opening_pence))}${a.opening_pence < 0 ? ' credit' : ''} as of ${dayLabel(a.as_of.slice(0, 10))}${a.opening_pence !== a.squeegee_pence ? ` (Squeegee said ${money(Math.abs(a.squeegee_pence))})` : ''}${a.state === 'incomplete' ? ' · history still arriving' : ''}`))
    for (const k of a.collections) if (k.state === 'collecting') box.append(el('div', { class: 'owed-collecting' }, el('span', {}, collectingWords(k, money))))
    const lines = a.lines ?? []
    if (lines.length) {
      const list = el('ul', { class: 'money-lines' })
      const item = (l: (typeof lines)[number]): HTMLElement => {
        const li = el('li', {})
        li.append(el('span', { class: 'money-when' }, l.on_date ? dayLabel(l.on_date) : ''))
        if (l.amount_pence === null) li.append(el('span', { class: 'money-note' }, l.text))
        else li.append(el('span', {}, l.text), el('span', { class: 'money-amt' }, `${l.amount_pence < 0 ? '-' : ''}${money(Math.abs(l.amount_pence))}`))
        li.title = lineWords(l, money)
        return li
      }
      for (const l of lines.slice(0, LINES_SHOWN)) list.append(item(l))
      box.append(list)
      if (lines.length > LINES_SHOWN) {
        const more = el('details', { class: 'money-more' }, el('summary', {}, `All ${lines.length} lines from Squeegee`))
        const rest = el('ul', { class: 'money-lines' })
        for (const l of lines.slice(LINES_SHOWN)) rest.append(item(l))
        more.append(rest)
        box.append(more)
      }
    }
  }
  if (m.unconfirmed_cash.length) box.append(el('p', { class: 'money-sub' }, `Paid cash, amount not recorded on ${m.unconfirmed_cash.length} visit${m.unconfirmed_cash.length === 1 ? '' : 's'}: confirm it on Owed.`))
  return box
}

// Past visits to review on this page are the Diary's own list (diary-sets.ts reviewVisits): a missed visit nobody has
// answered, unless a real visit is already booked ahead of it. That needs the customer as the customers list has them
// (next_booked and the rest), the read the Diary and Messages share. When it fails, every missed visit stays listed,
// as on the Diary: asking too much beats going quiet.
async function reviewSet(missed: Clean[]): Promise<Clean[]> {
  try {
    const [list, roundJobs] = await Promise.all([
      api<{ customers: Customer[] }>('/api/customers', 'customers'),
      severalJobsOn() ? loadRoundJobs() : Promise.resolve([] as CustomerJob[]),
    ])
    const row = list.customers.find((c) => c.id === customerId)
    return reviewVisits(missed, row ? [row] : null, roundJobs, todayISO())
  } catch {
    return missed
  }
}

function deleteZone(): HTMLElement {
  const del = el('button', { class: 'btn-danger delete-customer' }, 'Delete customer')
  del.addEventListener('click', async () => {
    // Done visits with no payment recorded: deleting the customer would
    // bury the unanswered question with them, so instead of the plain
    // confirm the button says what will happen to those visits.
    const unrecorded = cleans.filter((c) => c.state === 'done' && c.payment_status === 'unknown')
    const settlePrompt = (n: number, pence: number): boolean =>
      window.confirm(
        `${n} ${n === 1 ? 'visit' : 'visits'} with no payment recorded (${money(pence)}). Mark ${
          n === 1 ? 'it' : 'them'
        } paid and delete?`,
      )
    let settle = false
    if (unrecorded.length > 0) {
      settle = settlePrompt(unrecorded.length, unrecorded.reduce((s, c) => s + (c.amount_pence ?? 0), 0))
      if (!settle) return
    } else if (!window.confirm(`Delete ${customer!.name}? They will be hidden from every list, with their past visits. Roundly can't bring them back from here.`)) return

    const sendDelete = (withSettle: boolean): Promise<Ack> =>
      mutate({
        label: `Delete customer: ${customer!.name}`,
        method: 'DELETE',
        path: `/api/customers/${customer!.id}`,
        body: withSettle ? { version: customer!.version, settle_unrecorded: true } : { version: customer!.version },
        onAck: () => {
          toast(`${customer!.name} deleted`)
          window.location.hash = '#/grid'
        },
      })
    const ack = await sendDelete(settle)
    if (ack.ok) return
    if (ack.status === 409) {
      // The 409 the unrecorded refusal answers carries its numbers inside
      // the message (the only channel a rejected mutation has back to this
      // screen). The list above was stale — offer the same choice once
      // more, on the server's count.
      const served = /^(\d+) completed visits? have no payment recorded(?: \(([£$][\d.]+)\))?/.exec(ack.error ?? '')
      const parked = getQueue().find(
        (q) => q.status === 'conflict' && q.method === 'DELETE' && q.path === `/api/customers/${customer!.id}`,
      )
      if (served && parked) {
        if (window.confirm(`${Number(served[1])} visits with no payment recorded${served[2] ? ` (${served[2]})` : ''}. Mark them paid and delete?`)) {
          // Chosen: resolve the parked attempt so the retry is not held
          // behind it forever (the queue keeps same-row edits in order).
          dismiss(parked.key)
          const again = await sendDelete(true)
          if (!again.ok && again.status >= 400 && again.status < 500) window.alert(again.error ?? 'rejected')
          return
        }
        // Declined: the question is answered — don't leave it parked.
        dismiss(parked.key)
        void load()
        return
      }
      window.alert(ack.error ?? 'conflict')
      // Answered: the refusal (money still owed, or someone else changed
      // the customer) has been read. Left parked it showed as "needs review"
      // and held the next Delete behind it (Liam's iPhone, 26 Sep).
      if (parked) dismiss(parked.key)
      void load()
    } else if (ack.status >= 400 && ack.status < 500) {
      window.alert(ack.error ?? 'rejected')
    }
  })
  // Delete used to sit in normal flow directly under "Book clean" — two
  // adjacent taps on a phone, one of which removes the customer. Folded away
  // behind a disclosure so it cannot be hit by a mis-tap.
  const danger = el('details', { class: 'danger-zone' })
  danger.append(el('summary', {}, 'Danger zone'), del)
  return danger
}

async function load(): Promise<void> {
  const gen = routeGen // codex HIGH-3: bail before render if the route changed mid-load
  const root = document.querySelector<HTMLElement>("#screen")!
  try {
    const on = severalJobsOn()
    const [data, jobData, baCard] = await Promise.all([
      api<{ customer: Customer; cleans: Clean[]; linked_leads?: Lead[]; money?: CustomerMoney; first_clean?: { ids: number[]; date: string } | null; offer_facts?: OfferFacts }>(`/api/customers/${customerId}`),
      on ? api<{ jobs: CustomerJob[] }>(`/api/customers/${customerId}/jobs`).catch(() => null) : Promise.resolve(null),
      // Up next's Book again or Make them a regular (owner only; book-again.ts).
      isWorker() ? Promise.resolve(null) : fetchCustomerBookAgain(customerId),
    ])
    if (gen !== routeGen) return
    customer = data.customer
    cleans = data.cleans
    firstCleanData = data.first_clean ?? null
    broughtMoney = data.money ?? null
    jobs = jobData?.jobs ?? []
    const today = todayISO()
    const missed = cleans.filter((cl) => needsReview(cl, today) && !isContact(cl))
    const review = missed.length ? await reviewSet(missed) : []
    if (gen !== routeGen) return
    clear(root)
    const c = customer
    const page = el('div', { class: 'cust-page' })
    root.append(page)
    for (const lead of isWorker() ? [] : data.linked_leads ?? []) if (lead.has_extra_offer) {
      const open = el('button', { type: 'button', class: 'link-button' }, 'Open the enquiry')
      open.addEventListener('click', () => void import('./new-jobs').then(m => m.openNewJobsAt(lead.id)))
      page.append(el('div', { class: 'notice customer-pending-offer' }, el('p', {}, "Offered extras weren't added: open the enquiry to add them"), open))
    }

    // ---- who they are, and how to reach them ----
    const head = el('div', { class: 'detail-head cust-head' })
    // The back link always said "grid" even when the user arrived from the schedule, sending Lewis to a 261-row
    // spreadsheet instead of his day. Back to wherever they came from (Astra review 2026-09-25).
    const BACK: Record<string, { href: string; label: string }> = {
      schedule: { href: '#/schedule', label: '← The diary' },
      today: { href: '#/today', label: '← Today' },
      'new-jobs': { href: '#/new-jobs', label: '← New jobs' },
      owed: { href: '#/owed', label: '← Owed' },
      rebook: { href: '#/rebook', label: '← Messages' },
      analysis: { href: '#/analysis', label: '← Money' },
      history: { href: '#/history', label: '← History' },
    }
    const from = BACK[cameFrom] ?? { href: '#/grid', label: '← Work' }
    const id = c.id
    // ⋯: the two things the page does less often (the follow-up form without its Send, which Message already is).
    const more = rowMenu(`More for ${c.name}`, (list, close) => {
      const log = el('button', { type: 'button' }, 'Log completed work')
      log.addEventListener('click', () => { close(); openBooking({ customerId: id, completed: true }) })
      const followup = el('button', { type: 'button' }, 'Contact follow-up')
      const followupRecord = c
      followup.addEventListener('click', () => { close(); openFollowupDialog('customers', followupRecord, { send: false }) })
      list.append(log, followup)
    })
    more.classList.add('cust-more')
    head.append(el('div', { class: 'cust-topline' }, el('a', { href: from.href, class: 'back-link' }, from.label), more))
    // Redesign (Liam 2026-09-25, Astra-reviewed; again 9 Oct 2026): their name big, then where they are and how to
    // reach them, then the numbers you check most.
    const title = el('h2', { class: 'customer-name' }, c.name)
    // The pencil beside the name opens Details on the Name row, ready to type.
    const rename = el('button', { type: 'button', class: 'cust-name-edit', 'aria-label': `Edit name: ${c.name}`, title: 'Edit name' }, pencil())
    rename.addEventListener('click', () => {
      document.querySelector<HTMLButtonElement>('.cust-page .lemon-switch button[data-view="details"]')?.click()
      const row = document.querySelector<HTMLButtonElement>('.cust-page button[aria-label="Edit Name"]')
      row?.click()
      document.querySelector<HTMLElement>('.cust-page .fields input')?.scrollIntoView?.({ block: 'center' })
    })
    title.append(rename)
    // "Maybe lapsed" is not a standing (the pill says those), so it keeps its own badge.
    if (c.maybe_lapsed && c.status !== 'lapsed') title.append(el('span', { class: 'badge badge-unpaid' }, 'Maybe lapsed'))
    head.append(title)
    const where = addressLine(c.address, c.postcode, ', ')
    // codex MEDIUM-10: maps_link is imported free text, so a link only when it's https, never a javascript:/data: URL.
    const mapHref = c.maps_link && /^https:\/\//i.test(c.maps_link)
      ? c.maps_link
      : where ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(where)}` : null
    if (where) head.append(mapHref ? el('a', { href: mapHref, target: '_blank', rel: 'noopener', class: 'customer-address' }, where) : el('span', {}, where))
    else if (mapHref) head.append(el('a', { href: mapHref, target: '_blank', rel: 'noopener', class: 'customer-address' }, 'Map ↗'))
    // Pin the exact spot, or Exact spot pinned (27 Oct 2026, exact-spot.ts): the address and Directions then go there.
    if (!isWorker()) head.append(spotButton({ id: c.id, name: c.name, link: c.maps_link ?? null }, () => void load()))
    if (c.phone) head.append(el('span', { class: 'customer-phone' }, c.phone))
    // Where they stand, in the shared words (visit-words.ts standingWords). Not a second box: it opens the one box in
    // Details, as Work's Status cell does (?stand=1), the same box as on their Messages card.
    const now = standingOf(c)
    const pill = el('button', { type: 'button', class: 'customer-standing-pill', 'aria-label': `${standingWords(c)}: change where they stand` },
      ...(now === 'round' ? ['On the ', el('span', { class: 'brand-round' }, 'round')] : [standingWords(c)]),
      el('span', { class: 'roundly-select-arrow', 'aria-hidden': 'true' }))
    head.append(pill)
    // When they were last contacted or are to be asked again (the follow-up form), unless the pill already says it.
    const contactSummary = now === 'later' ? '' : followupSummary(c, today, 'customer')
    if (contactSummary) head.append(el('p', { class: 'cust-followup' }, contactSummary))
    page.append(head)

    const quick = el('div', { class: 'cust-quick' })
    // Only a call link when there is actually something dialable: 14 imported phone values are words like "Canvassing".
    const tel = telHref(c.phone)
    if (tel) quick.append(el('a', { href: tel, class: 'contact-btn cust-call', 'data-call-name': c.name }, el('span', { class: 'cust-icon cust-icon-call', 'aria-hidden': 'true' }), 'Call'))
    // How they are messaged (0036): a button that opens a blank message their way, and the ▾ to change the way. With no
    // way that works (no mobile, no email, or they want none), just the words and the ▾.
    const way = wayFor(c)
    const quickLink = linkFor(way, c, '', `From ${businessSettings().business_name}`)
    const wayBox = el('span', { class: 'customer-way cust-way' })
    if (quickLink.href) {
      const quickSend = el('a', { href: quickLink.href, target: way === 'whatsapp' ? '_blank' : '_self', rel: 'noopener', class: `contact-btn contact-${way === 'whatsapp' ? 'wa' : way}` }, el('span', { class: 'cust-icon cust-icon-chat', 'aria-hidden': 'true' }), wayWords(way).short)
      if(way==='email')bindEmailSend(quickSend,()=>({href:quickLink.href!,name:c.name,confirm:false,sent:()=>{}}))
      wayBox.append(quickSend)
    }
    const menu = wayMenu(c, () => void load(), { onConflict: () => void load() })
    if (!quickLink.href) {
      // Nothing to send to (Liam's testers, 19 Oct 2026: the arrow on "No mobile on file" was the only thing that did
      // anything). The whole tile adds what is missing for the way they are messaged, and only that; with a number on
      // file that cannot take it (a landline), or Don't message, the tile opens the way menu.
      const field = way === 'email' ? 'email' : 'phone'
      // A commercial customer set to Don't message with nothing an invoice can go to (Invoices on; commercial AGREED.md
      // items 12 and 15, the Commercial switch still waiting included): "+ Add mobile" where it read "Not messaged",
      // opening the add box for invoices (a mobile or an email, their way untouched).
      const forInvoice = way === 'none' && missingDetails(c).number
      const empty = forInvoice || (way !== 'none' && !String((c as unknown as Record<string, unknown>)[field] ?? '').trim())
      const missing = el('button', { type: 'button', class: `customer-way-missing${empty ? ' customer-way-add' : ''}` },
        empty ? el('span', { class: 'customer-way-plus', 'aria-hidden': 'true' }, '+') : '', empty ? (field === 'email' ? 'Add email' : 'Add mobile') : way === 'none' ? 'Not messaged' : quickLink.missing ?? '')
      missing.addEventListener('click', () => {
        if (forInvoice) openAddDetails(c, { need: { number: true }, onSaved: () => void load() })
        else if (empty) openContactSheet(c, field, () => void load())
        else { for (const other of document.querySelectorAll<HTMLDetailsElement>('details.way-menu[open]')) if (other !== menu) other.open = false; menu.open = true }
      })
      wayBox.append(missing)
    }
    wayBox.append(menu)
    wayBox.title = c.message_by ? `Messages go by ${wayWords(way).short}` : `Messages go your usual way (${wayWords(way).short})`
    quick.append(wayBox)
    const offerFacts = data.offer_facts
    let offerAction:HTMLButtonElement|null=null
    if (!isWorker() && offerFacts) {
      const offer = el('button', {type:'button', class:'link-button customer-offer-open'}, 'Offer another job')
      offer.addEventListener('click', () => openCustomerOffer(c, cleans, offerFacts, () => void load(),jobs))
      offerAction=offer as HTMLButtonElement
    }
    const listed = !!c.customer_list || takenOff(c)
    // Message opens their card in Messages with Send on screen, as Needs you's Message does (Liam's rule 3). Not for
    // someone on Recanvass or Taken off, as before.
    if (!listed) {
      const message = el('button', { type: 'button', class: 'cust-message' }, el('span', { class: 'cust-icon cust-icon-send', 'aria-hidden': 'true' }), 'Message')
      message.addEventListener('click', () => openMessagesFor({ id: c.id, name: c.name }, 'customer'))
      quick.append(message)
    }
    // Chat (0060): their conversation in Messages' Chats, every text, WhatsApp and email in one place.
    if (!isWorker()) quick.append(el('a', { href: `#/chats?customer=${c.id}`, class: 'cust-message cust-chat' }, el('span', { class: 'cust-icon cust-icon-chat', 'aria-hidden': 'true' }), 'Chat'))
    // Directions from where the phone is: to their exact spot when pinned, else the address (27 Oct 2026).
    const goTo = directionsHref(c.maps_link, where)
    if (goTo) quick.append(el('a', { href: goTo, target: '_blank', rel: 'noopener', class: 'cust-message cust-directions' }, lineDrawing('navigate', 18), 'Directions'))
    if(offerAction)quick.append(offerAction)
    page.append(quick)
    // Calls inside Roundly (calls.ts): this customer's recent calls.
    const calls = customerCalls(c.id)
    if (calls) page.append(calls)

    // ---- Up next (Liam, 29 Oct 2026, design C1 v3; up-next.ts): what they need from you, money first ----
    const owedNow = cleans.filter((x) => x.state === 'done' && x.payment_status === 'unpaid' && x.carryover !== 1).sort((a, b) => a.date.localeCompare(b.date))
    const reviewIds = new Set(review.map((cl) => cl.id))
    if (!isWorker()) {
      const who = firstName(c.name) || c.name
      const steps: UpNextStep[] = []
      for (const cl of owedNow) steps.push({ id: `pay:${cl.id}`, title: 'Take their payment', body: owedCard(cl) })
      for (const cl of [...review].sort((a, b) => a.date.localeCompare(b.date))) steps.push({ id: `review:${cl.id}`, title: 'Say what happened to a past visit', body: visitCard(cl, 'review') })
      // A check still open on their page, a manual one too (its card no longer says manual once opened).
      if (baCard && baCard.state !== 'done') {
        const gap = baCard.manual ? usualGapWeeks(baCard.done_dates) : null
        steps.push({
          // One id however the check goes (a check opened is no longer "manual" on its card, and is not done).
          id: 'book-again',
          title: baCard.manual ? `Make ${who} a regular` : 'Book again',
          line: baCard.manual
            ? `${gap ? `Cleaned about every ${gap} weeks. ` : ''}${baCard.state === 'waits' ? 'After their payment: check' : 'Check'} on WhatsApp how often they'd like cleaning, with their prices.`
            : undefined,
          waits: baCard.state === 'waits',
          // Their own "make them a regular" check waits behind the payment above it, in a line, not a second card.
          body: baCard.manual && baCard.state === 'waits' ? null : bookAgainCard(baCard, { reload: () => void load(), where: 'step' }),
        })
      }
      const firstUp = cleans.filter((x) => x.state === 'booked' && x.date >= today && !isContact(x) && !x.blocked_reason).sort((a, b) => a.date.localeCompare(b.date))[0]
      const listedNow = !!c.customer_list || takenOff(c)
      if (firstUp && !listedNow && firstUp.confirmed !== 1 && (firstUp.source === 'suggested' || firstUp.source === 'tentative') && !c.message_reply) {
        const ask = el('button', { type: 'button', class: 'link-button up-next-go' }, 'Message')
        ask.addEventListener('click', () => openMessagesFor({ id: c.id, name: c.name }, 'customer'))
        steps.push({ id: `ask:${firstUp.id}`, title: 'Ask about their next clean', line: `${shortDate(firstUp.date)} · Not asked yet`, body: ask })
      }
      if (offerFacts) {
        if(offerFacts.stop_suggestion)steps.push({id:'offer-stop',title:`Stop offers to ${who}?`,body:offerStopCard(c,offerFacts.stop_suggestion,()=>void load())})
        for (const offer of offerFacts.offers.filter(o => o.send_state !== 'withdrawn' && !o.booked_visit_id && o.acceptance_state !== 'none')) {
          steps.push({ id: `extra-offer:${offer.id}`, title: offer.acceptance_state === 'waiting' ? 'Your offer of another job' : 'They said yes to extra work', body: customerOfferCard(c, offer, cleans, () => void load()) })
        }
        const reason = offerReason(c, jobs, offerFacts)
        if (reason) {
          const offer = el('button', { type:'button', class:'link-button' }, 'Offer another job')
          offer.addEventListener('click', () => openCustomerOffer(c, cleans, offerFacts, () => void load(),jobs))
          steps.push({id:'offer-idea',title:'A job to ask about',line:reason,body:offer})
        }
      }
      const gaps = missingDetails(c)
      if (gaps.address || gaps.number) {
        const add = el('button', { type: 'button', class: 'link-button up-next-go' }, gaps.address && gaps.number ? 'Add details' : gaps.address ? 'Add address' : 'Add number')
        add.addEventListener('click', () => openAddDetails(c, { onSaved: () => void load() }))
        steps.push({ id: `details:${gaps.address ? 'a' : ''}${gaps.number ? 'n' : ''}`, title: gaps.address && gaps.number ? 'Add their address and number' : gaps.address ? 'Add their address' : 'Add their number',
          line: gaps.address ? 'For the map and Directions' : 'So you can message them', body: add })
      }
      // A Book again read that failed: its step is unknown, so nothing counts as done this time.
      const upNext = upNextSection(c.id, who, steps, baCard === undefined ? ['book-again'] : [])
      if (upNext) page.append(upNext)
    }

    // ---- the numbers, and money brought over ----
    const jobsCtx: JobsContext | null = on && jobData ? { customer: c, cleans, jobs, reload: () => void load() } : null
    const boxed = jobsCtx !== null && showsJobsBox(jobsCtx)
    // "Set to 4 months" under a guessed cycle.
    page.append(customerFigures(c, cleans, null, (days, label) => setCycleTo(c, days, label), isWorker() ? undefined : { price: () => openPriceSheet(c, () => void load()), every: () => openEverySheet(c, () => void load()) }))
    // Several jobs (Liam Q2): "Add another job" right under the price (its own row, so the three figures stay in one row
    // on a phone), glowing once when just switched on.
    if (jobsCtx && !boxed) page.append(el('div', { class: 'customer-tile cust-add-job' }, addJobButton(jobsCtx)))
    // Book again's finished line (0094): under Price and Every once the check is over (Up next holds it until then).
    if (baCard && baCard.state === 'done') page.append(el('div', { class: 'cust-book-again' }, bookAgainCard(baCard, { reload: () => void load(), where: 'customer' })))
    // What they owe or hold, brought from Squeegee (0043): straight under the money, seen before anything else.
    if (broughtMoney) page.append(moneyPanel(broughtMoney))
    if (jobsCtx && boxed) page.append(jobsBox(jobsCtx))

    // What asks for something (a visit to review, money not paid) is in Up next above; a worker keeps it here.
    const asks = el('div', { class: 'cust-asks' })
    if (isWorker()) {
      for (const cl of [...review].sort((a, b) => a.date.localeCompare(b.date))) asks.append(visitCard(cl, 'review'))
      for (const cl of owedNow) asks.append(owedCard(cl))
      if (asks.children.length) page.append(asks)
    }

    const reminders = cleans.filter((x) => isContact(x))
    const upcoming = cleans.filter((x) => x.state === 'booked' && x.date >= today && !isContact(x)).sort((a, b) => a.date.localeCompare(b.date))
    const hasBookedVisit = upcoming.some((x) => !x.blocked_reason)
    // Book a visit: not for someone Taken off with nothing booked, as before.
    const canBook = hasBookedVisit || !takenOff(c)
    // They asked to be left until a date (Ask again later): Book a visit stays, but not as the page's main button.
    const later = askAgainLater(c, today) && !hasBookedVisit
    const book = el('button', { type: 'button', class: hasBookedVisit ? 'cust-book' : later ? 'btn-secondary cust-book' : 'btn-primary cust-book' }, hasBookedVisit ? '+ Book a visit' : 'Book a visit')
    book.addEventListener('click', () => openBooking({ customerId: id }))
    const next = el('section', { class: 'next-visit-box', 'aria-label': 'Next visit' })
    next.append(el('div', { class: 'cust-section-head' }, el('h3', {}, 'Next visit'), hasBookedVisit && canBook ? book : null))
    // Off the round (Recanvass, Taken off) with visits still in the diary: the same warning as Work's lists, from this
    // page's own visits (the single customer read has no future_visit_count for the box in Details to say it).
    const future = cleans.filter((x) => x.state === 'booked' && x.date >= today)
    if (listed && future.length) next.append(el('p', { class: 'customer-list-booking-warning' }, `${future.length} future booking${future.length === 1 ? '' : 's'} still in the diary. Review before anyone goes.`))
    if (upcoming.length) upcoming.forEach((cl, i) => next.append(visitCard(cl, i === 0 ? 'next' : 'upcoming')))
    if (!hasBookedVisit) {
      const due = dueDate({ ...c, last_clean: c.last_clean ?? cleans.filter((x) => x.state === 'done').map((x) => x.date).sort().at(-1) ?? null })
      next.append(el('p', { class: 'cust-nothing' }, upcoming.length ? 'Nothing else booked' : 'Nothing booked'))
      if (later && c.follow_up_on) next.append(el('p', { class: 'cust-later' }, `They asked to be left until ${shortDate(c.follow_up_on)}`))
      else if (due && !c.job_stopped_on && !listed) next.append(el('p', { class: 'muted' }, `Due ${shortDate(due)}`))
      if (canBook) next.append(book)
    }
    if (reminders.length) {
      next.append(el('h3', { class: 'cust-sub-head' }, 'Contact reminders'))
      for (const cl of reminders) next.append(visitCard(cl, 'contact'))
    }
    page.append(next)

    // ---- what the van needs at the door ----
    // On a computer At the door sits beside the parts above, as tall as they are together and no taller, so no empty
    // row opens up above Visits · Details · Analysis (Liam, 9 Oct 2026).
    const beside = 2 + Number(!!(jobsCtx && !boxed)) + Number(asks.children.length > 0)
    const door = el('section', { class: 'cust-door', 'aria-label': 'At the door', style: `--door-rows: ${beside}` }, el('h3', {}, 'At the door'))
    const doorFields = el('div', { class: 'fields' })
    renderFields(doorFields, DOOR_FIELDS, false)
    door.append(doorFields)
    page.append(door)

    // ---- Visits · Details · Analysis ----
    const panels = {
      visits: el('div', { class: 'customer-tab-panel cust-panel cust-panel-visits' }),
      details: el('div', { class: 'customer-tab-panel cust-panel customer-side' }),
      analysis: el('div', { class: 'customer-tab-panel cust-panel' }),
    }
    let analysisBuilt = false
    const showView = (which: 'visits' | 'details' | 'analysis', dir = 0): void => {
      tab = which
      for (const [key, panel] of Object.entries(panels)) panel.hidden = key !== which
      if (which === 'analysis' && !analysisBuilt) { analysisBuilt = true; void renderAnalysisTab(panels.analysis) }
      // The view slides in from the side tapped (the Diary's switch does the same; docs/ANIMATION-STANDARD.md).
      if (dir) {
        const shown = panels[which]
        shown.classList.remove('cust-in-next', 'cust-in-prev')
        void shown.offsetWidth
        shown.classList.add(dir > 0 ? 'cust-in-next' : 'cust-in-prev')
      }
    }
    const views = lemonSwitch('Show', [{ key: 'visits', text: 'Visits' }, { key: 'details', text: 'Details' }, { key: 'analysis', text: 'Analysis' }] as const, tab, (key, dir) => showView(key, dir))
    page.append(views, panels.visits, panels.details, panels.analysis)

    // Visits: the past ones, newest first, four in sight and the rest one tap away, then every change made to them.
    const RECENT = 4
    const past = cleans.filter((x) => !upcoming.includes(x) && !reminders.includes(x) && !reviewIds.has(x.id))
    const pastBox = el('section', { class: 'cust-past-box', 'aria-label': 'Past visits' }, el('h3', {}, 'Past visits'))
    const list = el('div', { class: 'clean-list' })
    for (const cl of past.slice(0, RECENT)) list.append(pastRow(cl))
    if (!past.length) list.append(el('p', { class: 'empty-note' }, 'No visits yet.'))
    pastBox.append(list)
    const older = past.slice(RECENT)
    if (older.length) {
      const box = el('details', { class: 'clean-history' })
      const total = older.reduce((s, x) => s + visitReceived(x), 0)
      box.append(el('summary', {}, `Show ${older.length} older visit${older.length === 1 ? '' : 's'}`, total > 0 ? el('span', { class: 'owed-sub' }, ` · ${money(total)} paid across them`) : null))
      const hist = el('div', { class: 'clean-list' })
      for (const cl of older) hist.append(pastRow(cl))
      box.append(hist)
      pastBox.append(box)
    }
    // Every change to the customer and their visits, read when first opened. No Undo here: that is on History.
    const changes = el('details', { class: 'clean-history cust-changes' }, el('summary', {}, 'Changes'))
    const changesBody = el('div')
    changes.append(changesBody)
    let changesBuilt = false
    changes.addEventListener('toggle', () => { if (changes.open && !changesBuilt) { changesBuilt = true; void renderChanges(changesBody) } })
    pastBox.append(changes)
    panels.visits.append(pastBox)
    // Invoices (0065): how they are invoiced, who to, what is waiting, and every invoice sent to them.
    if (invoicesHere()) panels.visits.append(customerInvoicesCard(c, cleans, () => void load()))

    // Details: the box shared with their Messages card (Customer context, Where they stand), then everything else
    // Roundly holds about them, and Delete at the very bottom, folded away so it cannot be hit by a mis-tap.
    panels.details.append(customerContext(c, () => void load(), { visits: cleans }))
    if (!isWorker() && offerFacts) panels.details.append(offerAgreementCard(c, offerFacts, () => void load(), cleans))
    if (!isWorker() && offerFacts) for (const offer of offerFacts.offers.filter(o => o.booked_visit_id || o.acceptance_state === 'none' || o.send_state === 'withdrawn')) panels.visits.append(customerOfferCard(c, offer, cleans, () => void load()))
    const fieldsWrap = el('div', { class: 'fields' })
    panels.details.append(el('h3', {}, 'Customer details'), fieldsWrap, deleteZone())
    renderFields(fieldsWrap)

    showView(tab)
    // The pill (and Work's Status cell, ?stand=1) opens the Where they stand box in Details.
    const openStand = (): void => {
      views.choose('details')
      const tile = panels.details.querySelector<HTMLElement>('.customer-standing-tile')
      if (tile && tile.getAttribute('aria-expanded') !== 'true' && typeof tile.click === 'function') tile.click()
      if (typeof tile?.scrollIntoView === 'function') tile.scrollIntoView({ block: 'center' })
    }
    pill.addEventListener('click', openStand)
    if (openStanding) { openStanding = false; openStand() }
  } catch (err) {
    if (gen !== routeGen) return
    // A queued delete that has just landed: the customer is gone, so go back to the list instead of showing "not
    // found" on their page.
    if (/not found/i.test((err as Error).message)) { toast('Customer deleted'); window.location.hash = '#/grid'; return }
    clear(root)
    loadError(root, (err as Error).message, () => void load())
  }
}

// Every change to the customer and their visits (the change log), in History's plain sentences: time and login beside
// each, a fix's note under it, the exact values folded away. No Undo here; that is on the History screen.
async function renderChanges(box: HTMLElement): Promise<void> {
  clear(box)
  const status = el('p', { class: 'muted' }, 'Loading…')
  box.append(status)
  try {
    const data = await api<{ events: AuditEvent[]; names?: HistoryNames }>(`/api/events?customer_id=${customerId}&limit=200`)
    status.remove()
    if (!data.events.length) { box.append(el('p', { class: 'empty-note' }, 'No changes recorded yet.')); return }
    const names = data.names ?? NO_NAMES
    const log = el('div', { class: 'customer-changes' })
    for (const ev of data.events) {
      const before = parseRow(ev.before_json)
      const after = parseRow(ev.after_json)
      const row = el('div', { class: 'customer-change' })
      // The same plain sentence as History: time and login beside it, a fix's
      // note under it, the exact values folded away.
      const { email, note } = whoDidIt(ev.actor, names)
      row.append(el('span', { class: 'customer-change-when' }, eventTime(ev.created_at)))
      const what = el('div', { class: 'customer-change-what' })
      what.append(el('strong', {}, eventSentence(ev, before, after, names)))
      if (email) what.append(el('span', { class: 'customer-change-who' }, email))
      if (note) what.append(el('span', { class: 'customer-change-note' }, note))
      const changes = fieldChanges(ev.action, before, after)
      if (changes.length) {
        const exact = el('details', { class: 'customer-change-exact' })
        exact.append(el('summary', {}, changes.length === 1 ? 'Exact change' : `Exact changes (${changes.length})`))
        exact.append(el('span', { class: 'muted' }, changes.map((ch) => `${ch.label}: ${ev.action === 'create' ? ch.to : ev.action === 'delete' ? ch.from : `${ch.from} → ${ch.to}`}`).join(' · ')))
        what.append(exact)
      }
      row.append(what)
      log.append(row)
    }
    box.append(log)
  } catch (err) {
    status.textContent = `Couldn't load the change log (${(err as Error).message}).`
  }
}

// Analysis: worked out on the spot from the cleans and the change log —
// lifetime value, rhythm against the agreed cycle, how they pay, reliability,
// price history. Nothing stored.
// "1 h 30 min", "45 min".
function minutesWords(m: number): string {
  const h = Math.floor(m / 60), r = m % 60
  return h ? `${h} h${r ? ` ${r} min` : ''}` : `${r} min`
}
// "Came from: Add it": Details, with the empty fields shown, on Came from ready to type.
function editCameFrom(): void {
  document.querySelector<HTMLButtonElement>('.cust-page .lemon-switch button[data-view="details"]')?.click()
  const find = (): HTMLButtonElement | null => document.querySelector<HTMLButtonElement>('.cust-page button[aria-label="Edit Came from"]')
  if (!find()) document.querySelector<HTMLButtonElement>('.cust-page .customer-side .field-toggle')?.click()
  find()?.click()
  document.querySelector<HTMLElement>('.cust-page .customer-side input[aria-label="Came from"]')?.scrollIntoView?.({ block: 'center' })
}
// "Record how they paid": the visits marked paid with no Cash or Bank, each one tap, with Undo. Only the way is saved:
// the day it was paid and the amount stay as they are.
function openRecordMethods(visits: Clean[]): void {
  const previous = document.activeElement as HTMLElement | null
  const dialog = el('dialog', { class: 'booking-dialog ask-day-dialog cust-methods-dialog', 'aria-labelledby': 'cust-methods-title' }) as HTMLDialogElement
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const list = el('div', { class: 'cust-methods' })
  let changed = false
  for (const cl of [...visits].sort((x, y) => y.date.localeCompare(x.date))) {
    const row = el('div', { class: 'cust-method-row' }, el('span', {}, `${dayLabel(cl.date)} · ${money(cl.amount_pence)}`))
    const pick = el('div', { class: 'cust-method-pick', role: 'group', 'aria-label': `How the ${dayLabel(cl.date)} visit was paid` })
    for (const m of ['cash', 'bank'] as const) {
      const b = el('button', { type: 'button', class: 'btn-secondary' }, m === 'cash' ? 'Cash' : 'Bank') as HTMLButtonElement
      b.addEventListener('click', () => {
        for (const x of pick.querySelectorAll<HTMLButtonElement>('button')) x.disabled = true
        void mutate({ label: `Paid by ${m}: ${customer!.name} ${cl.date}`, method: 'PATCH', path: `/api/cleans/${cl.id}`, body: { version: cl.version, payment_method: m }, onConflict: () => refresh() }).then((ack) => {
          if (!ack.ok && ack.status !== 0) { for (const x of pick.querySelectorAll<HTMLButtonElement>('button')) x.disabled = false; if (ack.status >= 400 && ack.status < 500) toast(`Couldn't save that: ${ack.error ?? 'refused'}`); return }
          changed = true
          pick.replaceWith(el('span', { class: 'cust-method-done' }, `✓ ${m === 'cash' ? 'Cash' : 'Bank'}`))
          undoPatch({ text: `The ${dayLabel(cl.date)} visit: paid by ${m}`, label: `Undo paid by ${m}: ${customer!.name} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version, body: { payment_method: null }, after: () => refresh() })
        })
      })
      pick.append(b)
    }
    row.append(pick)
    list.append(row)
  }
  dialog.append(el('header', { class: 'booking-head' }, el('h2', { id: 'cust-methods-title' }, 'How did they pay?'), close),
    el('div', { class: 'booking-content ask-day-content' }, el('p', { class: 'muted' }, 'These visits are marked paid, with no Cash or Bank. Tap how each was paid.'), list))
  close.addEventListener('click', () => dialog.close())
  dialog.addEventListener('close', () => { dialog.remove(); if (changed) refresh(); if (previous?.isConnected) previous.focus({ preventScroll: true }) })
  document.body.append(dialog)
  dialog.showModal()
}

async function renderAnalysisTab(box: HTMLElement): Promise<void> {
  clear(box)
  box.append(el('p', { class: 'muted' }, 'Working it out…'))
  let events: AuditEvent[] = []
  // The round's tier cut-offs for the tiers row on the player card (Extra features; null when tiers are off or unread).
  const tiers = api<{ row: { cutoffs: number[] | null } }>('/api/business/tiers').then((r) => r.row.cutoffs ?? null).catch(() => null)
  try {
    const data = await api<{ events: AuditEvent[] }>(`/api/events?customer_id=${customerId}&limit=500`)
    events = data.events
  } catch {
    // Without the change log the payment-speed line says "not known"; the rest still works.
  }
  const cutoffs = await tiers
  // Several jobs: each job read on its own visits, the money over all of them (PLAN 7).
  const extra = jobs.filter((j) => !j.deleted_at)
  const analysed = extra.length
    ? [
        { id: null, name: customer!.job_name?.trim() || kindLabel('window'), price_pence: customer!.reg_price_pence, cycle_days: customer!.cycle_days, stopped: !!customer!.job_stopped_on },
        ...extra.map((j) => ({ id: j.id, name: j.name, price_pence: j.price_pence, cycle_days: j.cycle_days, stopped: !!j.stopped_on })),
      ]
    : []
  // Owed on their account from Squeegee (0043), so this tile and Owed say the same figure.
  const firstCleanOf = firstCleanData?.ids.length ? { ...firstCleanData, counts: noFixedCycle(customer!) } : null
  const a = analyse(customer!, cleans, events, todayISO(), analysed, broughtMoney ? broughtMoney.accounts.reduce((s, x) => s + x.chaseable_pence, 0) : 0, firstCleanOf)
  clear(box)
  // Tier and job times first (Liam, 13 Oct 2026: "collect lots of data around the tier system and improve this page").
  box.append(jobTimesCard(customer!, cleans, cutoffs, firstCleanOf))
  box.append(el('p', { class: 'analysis-verdict' }, a.verdict))
  const grid = el('div', { class: 'analysis-grid' })
  const tile = (label: string, value: string, note?: string): HTMLElement => {
    const t = el('div', { class: 'analysis-tile' }, el('span', { class: 'rebook-fact-label' }, label), el('span', { class: 'analysis-value' }, value))
    if (note) t.append(el('span', { class: 'analysis-note' }, note))
    return t
  }
  // A tile that shows something missing has the one tap that fixes it (Liam, 9 Oct 2026); the rest stay what happened.
  const act = (t: HTMLElement, label: string, run: (b: HTMLButtonElement) => void): HTMLElement => {
    const b = el('button', { type: 'button', class: 'link-button analysis-act' }, label) as HTMLButtonElement
    b.addEventListener('click', () => run(b))
    t.append(b)
    return t
  }
  const c = customer!
  // How often they actually have it, set as their cycle: the nearest choice the Cycle list has.
  // Not for someone set to Just once (How often design D): a one-off is never guessed into a round.
  const asCycle = !a.perJob.length && !a.agreedCycleDays && a.actualGapDays && !c.job_stopped_on && cycleLabel(null, c.cycle_text) !== 'just once' ? nearestCycle(a.actualGapDays) : null
  const setIt = (t: HTMLElement): HTMLElement => asCycle ? act(t, `Set ${asCycle.label} as their cycle`, (b) => { b.disabled = true; setCycleTo(c, asCycle.days, asCycle.label) }) : t
  const missing = [!a.agreedCycleDays ? 'cycle' : null, c.reg_price_pence == null ? 'price' : null].filter(Boolean).join(' or ')
  const unrecorded = cleans.filter((x) => x.state === 'done' && x.payment_status === 'paid' && !x.payment_method && !x.deleted_at)
  const origin = c.origin?.trim()
  grid.append(
    tile('Lifetime value', money(a.lifetimePaidPence), `${a.lifetimeCleans} visit${a.lifetimeCleans === 1 ? '' : 's'}${a.customerSince ? ` since ${shortDate(a.customerSince)}` : ''}`),
    tile('Last 12 months', money(a.lastYearPaidPence), `${a.lastYearCleans} paid visit${a.lastYearCleans === 1 ? '' : 's'}`),
    // Says which is missing: a price set with no cycle used to read "no cycle or price agreed" (Liam's screenshot).
    setIt(tile('A year at the agreed cycle', a.projectedYearPence ? money(a.projectedYearPence) : '—', a.perJob.length ? 'every job still running, added up' : a.projectedYearPence && a.agreedCycleDays ? `${money(c.reg_price_pence)} every ${weeksLabel(a.agreedCycleDays)}` : `no ${missing} agreed`)),
    setIt(tile('Rhythm', a.actualGapDays ? `every ${weeksLabel(a.actualGapDays)}` : '—', a.agreedCycleDays ? `agreed ${weeksLabel(a.agreedCycleDays)}` : 'no cycle agreed')),
    tile('Average price', a.averagePricePence ? money(a.averagePricePence) : '—', a.priceHistory.length > 1 ? `${a.priceHistory.length - 1} price change${a.priceHistory.length === 2 ? '' : 's'}` : 'no price changes'),
    unrecorded.length ? act(tile('How they pay', a.cashCount || a.bankCount ? `${a.bankCount} bank · ${a.cashCount} cash` : '—', `${unrecorded.length} not recorded`), 'Record how they paid', () => openRecordMethods(unrecorded))
      : tile('How they pay', a.cashCount || a.bankCount ? `${a.bankCount} bank · ${a.cashCount} cash` : '—'),
    tile('Time to pay', a.daysToPay === null ? 'not known' : `${a.daysToPay} day${a.daysToPay === 1 ? '' : 's'}`, a.daysToPay === null ? 'no payment dates recorded in the app yet' : `from ${a.daysToPaySample} payment${a.daysToPaySample === 1 ? '' : 's'} logged here`),
    tile('Chasing', a.chasedCleans ? `${a.chasedCleans} visit${a.chasedCleans === 1 ? '' : 's'} chased` : 'never chased', a.chasesNeeded > a.chasedCleans ? `${a.chasesNeeded} messages in all` : undefined),
    tile('Owed now', a.owedPence ? money(a.owedPence) : 'nothing'),
    tile('Reliability', `${a.cancelled} cancelled · ${a.couldntDo} couldn't do`),
    tile('Quiet spells', a.quietSpells ? `${a.quietSpells}` : 'none', a.longestQuietDays ? `longest ${weeksLabel(a.longestQuietDays)}` : 'never more than two cycles apart'),
    // Where they came from (the enquiry's source when they came through New jobs): which adverts bring keepers.
    origin ? tile('Came from', origin) : act(tile('Came from', 'not recorded'), 'Add it', () => editCameFrom()),
    // The worker's Start and Finish on done visits, and what the round earns per hour at their house.
    tile('Time on the job', a.jobMinutes !== null ? `about ${minutesWords(a.jobMinutes)}` : 'not timed yet',
      [a.jobTimed ? `${a.jobTimed} visit${a.jobTimed === 1 ? '' : 's'} timed${a.perHourPence !== null ? ` · ${money(a.perHourPence)} an hour` : ''}` : 'from Start and Finish on Today',
        c.time_taken?.trim() ? `time taken says ${c.time_taken.trim()}` : null].filter(Boolean).join(' · ')),
    // How they answer when asked: first messages, and those answered with a reply or a day agreed.
    // Not "not asked yet": those are a visit's words (visit-words.ts), and this is about their messages.
    tile('Replies', a.replyAsks ? `${a.replyAnswered} of ${a.replyAsks} asks` : '—',
      a.replyDays !== null ? `usually within ${a.replyDays === 0 ? 'the day' : `${a.replyDays} day${a.replyDays === 1 ? '' : 's'}`}` : a.replyAsks ? 'no reply recorded yet' : 'no messages recorded yet'),
  )
  box.append(grid)
  // One line per job (several jobs): its own rhythm, price changes and a year's worth.
  if (a.perJob.length) {
    box.append(el('h3', {}, 'Each job'))
    const list = el('div', { class: 'analysis-jobs' })
    for (const j of a.perJob) {
      const bits = [j.stopped ? 'stopped' : j.rhythm, j.priceHistory.length > 1 ? `${j.priceHistory.length - 1} price change${j.priceHistory.length === 2 ? '' : 's'}` : null, j.quietSpells ? `${j.quietSpells} quiet spell${j.quietSpells === 1 ? '' : 's'}` : null, j.projectedYearPence ? `about ${money(j.projectedYearPence)} a year` : null].filter(Boolean)
      list.append(el('p', { class: 'analysis-job' }, el('strong', {}, j.name), ` · ${bits.join(' · ')}`))
    }
    box.append(list)
  }
  if (a.priceHistory.length) {
    box.append(el('h3', {}, 'Price history'))
    const list = el('div', { class: 'analysis-prices' })
    for (const p of a.priceHistory) list.append(el('span', { class: 'analysis-price' }, `${shortDate(p.date)} · ${money(p.pence)}`))
    box.append(list)
  }
}

export function mountCustomer(id: number): void {
  customerId = id
  tab = 'visits'
  editing = false // module state must not survive navigation (kimi S-5)
  refreshPending = false
  showAllFields = false
  const hash = window.location.hash
  openStanding = /[?&]stand=1\b/.test(hash)
  // Taken out of the address at once, so a reload or Back does not open it again (as Work's ?add=1).
  if (openStanding) history.replaceState(history.state, '', hash.replace(/\?.*$/, ''))
  // Where the user came from decides where "back" goes. Read at mount time,
  // before the hash changes again. The referrer test is the legacy fallback
  // for a cold load straight onto a customer — it never overrides a screen
  // the router actually recorded.
  if (cameFrom === 'grid' && /#\/schedule/.test(document.referrer)) cameFrom = 'schedule'
  void load()
}

// Set by the router so customer detail knows its return path.
export function noteScreenBeforeCustomer(screen: string): void {
  cameFrom = screen
}

export function customerOnDataChanged(): void {
  refresh()
}
